import type { InvoicePaymentCreation } from 'components/containers/invoice-payments/types'
import Big from 'big.js'
import { ROUNDING_PRECISIONS } from 'utils/constants'

export const calculateRemainder = (
  amountDue: number | Big,
  amountPaid: number | Big,
  adjustment: number | Big,
): number => {
  const amountDueBig = typeof amountDue === 'number' ? Big(amountDue) : amountDue
  const amountPaidBig = typeof amountPaid === 'number' ? Big(amountPaid) : amountPaid
  const adjustmentBig = typeof adjustment === 'number' ? Big(adjustment) : adjustment

  return amountDueBig.sub(amountPaidBig).sub(adjustmentBig).round(ROUNDING_PRECISIONS.COST).toNumber()
}

export function safeNumber(value: string | number) {
  const num = Number(value)
  return isNaN(num) ? 0 : num
}

/**
 * Distributes a payment amount proportionally across multiple invoices based on their individual amounts.
 * Handles both underpayments and overpayments.
 *
 * @param {number} paymentAmount - The total amount of payment to be distributed.
 * @param {InvoicePaymentCreation[]} selectedInvoices - An array of invoice objects to distribute the payment across.
 * @returns {Array<{id: string, amountPaid: number}>} An array of objects, each containing the invoice ID and amount paid for that invoice.
 *
 * @description
 * - Calculates the proportion of each invoice relative to the total invoice amount.
 * - Distributes the payment amount according to these proportions, rounded to the nearest cent.
 * - Handles both underpayments and overpayments.
 * - For overpayments, the excess is distributed proportionally across all invoices.
 * - Ensures that the total distributed amount exactly matches the input payment amount.
 * - Any remaining amount due to rounding is distributed to maintain exact total distribution.
 */
export const distributePaymentProportionally = (
  paymentAmount: number,
  selectedInvoices: InvoicePaymentCreation[],
  invoicesFormData: any,
): { id: string; amountPaid: number }[] => {
  const paymentAmountBig = Big(safeNumber(paymentAmount))
  const totalInvoiceAmount: Big = selectedInvoices.reduce((sum, invoice) => {
    const amountDue = Big(safeNumber(invoice.amountDue))
    const adjustment = Big(safeNumber(invoicesFormData[invoice.id]?.adjustment))
    return sum.plus(amountDue).sub(adjustment).round(ROUNDING_PRECISIONS.COST)
  }, Big(0))
  // Handle the case where totalInvoiceAmount is zero
  if (totalInvoiceAmount.eq(0)) {
    return selectedInvoices.map(invoice => ({
      id: invoice.id,
      amountPaid: 0,
    }))
  }
  let remainingPaymentAmount = paymentAmountBig

  // Initial proportional distribution
  const distributedInvoicePayments = selectedInvoices.map(invoice => {
    const amountDue = Big(safeNumber(invoice.amountDue))
    const adjustment = Big(safeNumber(invoicesFormData[invoice.id]?.adjustment))
    const adjustedAmountDue = amountDue.sub(adjustment)
    const distributedAmount = paymentAmountBig
      .mul(adjustedAmountDue)
      .div(totalInvoiceAmount)
      .round(ROUNDING_PRECISIONS.COST)
    remainingPaymentAmount = remainingPaymentAmount.sub(distributedAmount).round(ROUNDING_PRECISIONS.COST)
    return {
      id: invoice.id,
      amountPaid: distributedAmount.toNumber(),
    }
  })

  if (paymentAmountBig.eq(0)) {
    return distributedInvoicePayments
  }

  // Proportionally fix overpayments/underpayments that are introduced when rounding payments to the nearest cent
  // remainingTargetSum - the sum that all of the remaining distributed payments SHOULD add up to
  // remainingOriginalSum - the sum that all of the remaining distributed payments CURRENTLY add up to
  const { correctedDistributedPayments } = distributedInvoicePayments.reduce(
    (aggregate, invoice) => {
      const originalAmountPaid = Big(safeNumber(invoice.amountPaid))
      const correctedAmountPaid = aggregate.remainingOriginalSum.eq(0)
        ? originalAmountPaid
        : originalAmountPaid
            .mul(aggregate.remainingTargetSum)
            .div(aggregate.remainingOriginalSum)
            .round(ROUNDING_PRECISIONS.COST)
      const correctedPayment = {
        id: invoice.id,
        amountPaid: correctedAmountPaid.toNumber(),
      }
      return {
        correctedDistributedPayments: [...aggregate.correctedDistributedPayments, correctedPayment],
        remainingTargetSum: aggregate.remainingTargetSum.sub(correctedAmountPaid),
        remainingOriginalSum: aggregate.remainingOriginalSum.sub(originalAmountPaid),
      }
    },
    {
      correctedDistributedPayments: [],
      remainingTargetSum: paymentAmountBig,
      remainingOriginalSum: paymentAmountBig.sub(remainingPaymentAmount),
    } as {
      correctedDistributedPayments: { id: string; amountPaid: number }[]
      remainingTargetSum: Big
      remainingOriginalSum: Big
    },
  )

  return correctedDistributedPayments
}
